import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { PreviousPanelButton } from './PreviousPanelButton';
import { MainNavigationGroup } from '../MainNavigationGroup/MainNavigationGroup';
import { RefetchBlock } from '../../../../components/molecules/RefetchBlock';
import { GlobalDocumentIds } from '../../../../types/GlobalDocumentIds';
import { TopItem } from '../MainNavigationTopItem/TopItem';
import { CloseSubPanel, FetchSubPanel, MainNavigationGroupProps, PanelStateType } from '../../MainNavigationTypes';
import { SubPanelLoadingSpinner } from './SubPanelLoadingSpinner';
import { CloseAllSubPanelsButton } from './CloseAllSubPanelsButton';
import { SUB_PANEL_ANIMATION_DURATION } from '../../useMainNavigation';
import bem from '../../../../lib/bemUtils/bem';
import { RefetchBlockModifier } from '../../../../components/molecules/RefetchBlock/RefetchBlock';

type SubPanelType = {
  classIndex: number;
  closeSubPanel: CloseSubPanel;
  fetchSubPanel: FetchSubPanel;
  subPanelState: PanelStateType;
  closeAllSubPanels: () => void;
  panelLevel?: number;
  allPanelCount?: number;
};

export function SubPanel({
  classIndex,
  closeSubPanel,
  fetchSubPanel,
  subPanelState,
  closeAllSubPanels,
  panelLevel,
  allPanelCount,
}: SubPanelType) {
  const headlineLinkRef = useRef<HTMLAnchorElement>(null);
  const [activeOpenButtonId, setActiveOpenButtonId] = useState<string | undefined>(undefined);
  const panelNavigationStartingPoint = useRef<string>(undefined);

  /**
   * If next sub panel is closed, activeOpenButtonId of this panel  will be set to undefined,
   * so it is possible to close NextPanelButton without losing focus.
   * */
  useEffect(() => {
    if (panelNavigationStartingPoint.current) {
      // preventScroll is needed, because the sub panel container is scrollable (overflow hidden) and
      // the focused button would be shifted by slide animations, which would scroll the container otherwise
      document.getElementById(panelNavigationStartingPoint.current)?.focus({ preventScroll: true });
      panelNavigationStartingPoint.current = undefined;
    }

    if (panelLevel === allPanelCount && activeOpenButtonId) {
      panelNavigationStartingPoint.current = activeOpenButtonId;
      setActiveOpenButtonId(undefined);
    }
  }, [allPanelCount, panelLevel, activeOpenButtonId]);

  /**
   * Focusing the sub panel's headline when the sub panel is opened (data is loaded),
   * because the sub panel is hidden in the DOM while loading.
   * The focus is set after the slide animation, because the panel is still
   * visibility-hidden at the start of the animation (box visibility transition &
   * container visibility animation), which would make the focus fail.
   */
  useEffect(() => {
    if (subPanelState.panelData) {
      const timeout = window.setTimeout(() => {
        // preventScroll is needed, because the sub panel container is scrollable (overflow hidden) and
        // the focused panel is shifted by its slide animation, which would scroll the container otherwise
        headlineLinkRef?.current?.focus({ preventScroll: true });
      }, SUB_PANEL_ANIMATION_DURATION);
      return () => {
        window.clearTimeout(timeout);
      };
    }
    return undefined;
  }, [subPanelState.panelData]);

  const refetchBlock = () => {
    if (
      subPanelState.fetchUrl &&
      subPanelState.panelName &&
      subPanelState.panelLink &&
      subPanelState.nextPanelButtonRef
    ) {
      fetchSubPanel(
        subPanelState.fetchUrl,
        subPanelState.panelLevel,
        subPanelState.panelName,
        subPanelState.panelLink,
        subPanelState.nextPanelButtonRef,
        subPanelState.loadingError
      );
    }
  };

  return (
    <div
      id={`${GlobalDocumentIds.mainNavigationSubPanel}${classIndex}`}
      className={bem('main-navigation__sub-panel', String(classIndex))}
    >
      <TopItem>
        {subPanelState?.panelLink && subPanelState?.panelName && subPanelState?.fetchUrl && (
          <>
            <PreviousPanelButton
              panelName={subPanelState?.panelData?.parent?.navItem?.title}
              closeSubPanel={closeSubPanel}
              panelLevel={subPanelState.panelLevel}
            />
            <h2 className="main-navigation__sub-panel-headline">
              <Link
                to={subPanelState.panelLink}
                ref={headlineLinkRef}
                className="main-navigation__sub-panel-headline-link font-medium-15-19"
              >
                {subPanelState.panelName}
              </Link>
            </h2>
          </>
        )}
        {/* show close button only on last panel */}
        {panelLevel === allPanelCount && <CloseAllSubPanelsButton closeAllSubPanels={closeAllSubPanels} />}
      </TopItem>
      {/* loading */}
      <div className="main-navigation__scroll-container">
        {!subPanelState?.panelData && !subPanelState.loadingError && <SubPanelLoadingSpinner />}
        {/* loading error */}
        {!subPanelState?.panelData && subPanelState?.loadingError && subPanelState?.fetchUrl && (
          <RefetchBlock modifier={RefetchBlockModifier.APPLICATION} refetchBlock={refetchBlock} />
        )}
        {subPanelState.panelData?.groups?.map((mainNavigationGroup: MainNavigationGroupProps) => (
          <MainNavigationGroup
            key={mainNavigationGroup.groupLabel}
            groupLabel={mainNavigationGroup.groupLabel}
            navItems={mainNavigationGroup.navItems}
            fetchSubPanel={fetchSubPanel}
            panelLevel={subPanelState.panelLevel + 1}
            activeOpenButtonId={activeOpenButtonId}
            setActiveOpenButtonId={setActiveOpenButtonId}
          />
        ))}
      </div>
    </div>
  );
}
